import type { TFunction } from 'i18next';

import type { FilterDef, FilterOption } from '@nucleus-commerce/admin-kit/index-table';
import { CustomerStatus, type Customer } from '@nucleus-commerce/api-client/admin';

/** Fields GET /customers sorts on; anything else is a 400. */
export const CUSTOMER_SORTS = ['name', 'ordersCount', 'totalSpent', 'lastOrderAt'] as const;

const valuesOf = (values: Record<string, string>): FilterOption[] =>
  Object.values(values).map((value) => ({ value, label: value }));

/**
 * The filters as the URL and the API spell them, for the route's search schema and loader. The
 * enum `options` drop unknown values the same way the page's filters do, so the prefetch and the
 * page share a query key. Any `currency` marks `totalSpent` as whole minor units; the page shows
 * it in the store's currency.
 */
export const CUSTOMER_FILTER_FIELDS = [
  { key: 'status', type: 'enum', options: valuesOf(CustomerStatus) },
  { key: 'tag', type: 'text' },
  { key: 'acceptsMarketing', type: 'boolean' },
  { key: 'ordersCount', type: 'number-range' },
  { key: 'totalSpent', type: 'number-range', currency: 'USD' },
  { key: 'lastOrderAt', type: 'date-range' },
] as const;

export interface CustomerFiltersOptions {
  /** The `customers` namespace. */
  t: TFunction;
  currency: string;
}

/** Filters of the customers table, in the order the filter menu lists them. */
export function customerFilters({
  t,
  currency,
}: CustomerFiltersOptions): Array<FilterDef<Customer>> {
  return [
    {
      key: 'status',
      label: t('filters.status'),
      type: 'enum',
      pinned: true,
      options: Object.values(CustomerStatus).map((value) => ({
        value,
        label: t(`status.${value}`),
      })),
    },
    {
      key: 'acceptsMarketing',
      label: t('filters.acceptsMarketing'),
      type: 'boolean',
      pinned: true,
      aliases: ['marketing'],
      trueLabel: t('marketing.subscribed'),
      falseLabel: t('marketing.notSubscribed'),
    },
    { key: 'tag', label: t('filters.tag'), type: 'text', placeholder: t('filters.tagPlaceholder') },
    {
      key: 'ordersCount',
      label: t('filters.ordersCount'),
      type: 'number-range',
      min: 0,
      step: 1,
      aliases: ['orders'],
    },
    {
      key: 'totalSpent',
      label: t('filters.totalSpent'),
      type: 'number-range',
      currency,
      min: 0,
      aliases: ['spent'],
    },
    {
      key: 'lastOrderAt',
      label: t('filters.lastOrderAt'),
      type: 'date-range',
      aliases: ['lastOrder'],
    },
  ];
}
